// FACTURACIÓN — pestaña de plata del directivo (mockup del responsable,
// validado 2026-07-05). Resumen ejecutivo por CORTE (Q1 1–15 · Q2 16–22 ·
// Q3 23–fin): la pestaña navega SOLO por cortes (decisión del responsable —
// la OP factura completa en el corte de su conciliación).
//
//   - 4 KPIs: facturado del corte (+proyección = facturado + pendiente),
//     nivel de servicio (cumplimiento / REAL), facturado del año (delta vs
//     corte anterior — no hay histórico 2025), pendiente por responsable.
//   - Corte por área + tiendas · Puente producido → facturado (estimados a
//     precio vigente, con residuo que cierra la suma).
//   - Nivel de servicio semanal (4 series + chips de área) · Órdenes del
//     corte (terminadas / en proceso / sin programar / tiendas FACTURADA
//     v1 sin cruce) · Tendencia de cortes.
//
// Gate: puede_ver_costos (la entrada del menú no aparece sin la capacidad;
// el API igual responde 403 si se revoca).

import { useState } from 'react';
import type { DirectivoFacturacion, FacturacionOrden } from 'schemas';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '../../components/ui/card';
import { KpiCard } from '../../components/ui/kpi-card';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { Spinner } from '../../components/ui/spinner';
import { DirectivoLayout } from '../../components/layout/DirectivoLayout';
import { useDirectivoFacturacion } from '../../lib/api/queries';
import { useTenantConfig } from '../../lib/api/useTenantConfig';
import { cn } from '../../lib/utils';
import {
  BarraPeriodo,
  FMT_DIA_CORTO,
  FMT_DIA_MES,
  hoyIso,
  limpiar,
  numeroSemanaIso,
  parseUTC,
  rangoDeCorte,
  semanaIsoKeyDe,
  usePeriodo,
} from './lib/periodo';
import { copM, miles } from './lib/formato';

export function FacturacionPage() {
  const [plantaSel, setPlantaSel] = useState<string | null>(null);
  // Para el corte, el rango del preset se manda COMPLETO (sin recortar a hoy):
  // el backend detecta el corte exacto y activa barra de días + proyección.
  const ctrl = usePeriodo(undefined, 'corte', { url: true });
  const { preset, periodo } = ctrl;
  const rango =
    preset === 'corte'
      ? (() => {
          const dia = Number(periodo.desde.slice(8, 10));
          const q = dia <= 15 ? 1 : dia <= 22 ? 2 : 3;
          return rangoDeCorte(periodo.desde.slice(0, 7), q);
        })()
      : { desde: periodo.desde, hasta: periodo.hasta };

  const fact = useDirectivoFacturacion({ ...rango, plantaId: plantaSel });

  return (
    <DirectivoLayout>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <BarraPeriodo ctrl={ctrl} />
        <div className="flex-1" />
        {fact.data && fact.data.plantas.length > 1 ? (
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Planta
            </span>
            <SegmentedControl
              value={plantaSel ?? 'todas'}
              onChange={(v) => setPlantaSel(v === 'todas' ? null : v)}
              size="sm"
              options={[
                { value: 'todas', label: `Todas (${fact.data.plantas.length})` },
                ...fact.data.plantas.map((p) => ({ value: p.id, label: p.nombre })),
              ]}
            />
          </div>
        ) : null}
      </div>
      <p className="mb-4 text-[11px] text-fg-tertiary">
        Facturación /{' '}
        {plantaSel === null
          ? 'Todas'
          : (fact.data?.plantas.find((p) => p.id === plantaSel)?.nombre ?? '')}
      </p>

      {fact.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : fact.isError || !fact.data ? (
        <Card className="p-6 text-sm text-fg-secondary">
          No se pudo cargar la facturación. Intente de nuevo en unos segundos.
        </Card>
      ) : (
        <FacturacionContenido data={fact.data} />
      )}
    </DirectivoLayout>
  );
}

function FacturacionContenido({ data }: { data: DirectivoFacturacion }) {
  const facturadoCorte = data.facturado.valor_total + data.tiendas.valor_total;
  const ns = data.nivel_servicio;
  const nsCorte = ns.corte;
  const nsAcum = ns.acumulado_anio;
  const pctR = (v: number | null) => (v !== null ? `${Math.round(v)}%` : 'n/s');
  const deltaAnio = data.anio.delta_pct_vs_corte_anterior;

  return (
    <>
      {/* ── Fila de KPIs ── */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="py-4">
          <KpiCard
            label={data.corte.q_key !== null ? 'Facturado del corte' : 'Facturado del período'}
            hint={`Armado ${copM(data.facturado.valor_total)} (semanal ${copM(data.facturado.valor_semanal)} + dominical ${copM(data.facturado.valor_dominical)}, ${data.facturado.ops_con_recargo} OPs con recargo ×1.8) + tiendas ${copM(data.tiendas.valor_total)}. Solo producción validada de OPs terminadas.`}
            value={copM(facturadoCorte)}
            sub={`${miles(data.facturado.unidades_validadas)} paq validadas · ${data.facturado.ops} OPs terminadas`}
            trend={
              data.corte.en_curso
                ? {
                    direction: 'up',
                    text: `Proyección al cierre: ${copM(data.proyeccion_cierre_cop)}`,
                    tone: 'neutral',
                  }
                : undefined
            }
          />
        </Card>

        <Card className="py-4">
          <KpiCard
            label={
              data.corte.q_key !== null
                ? 'Nivel de servicio del corte'
                : 'Nivel de servicio del período'
            }
            hint="REAL = (realizadas + no entregadas justificadas por causa CLIENTE) / programadas — nunca supera 100%. Es el argumento contractual con el cliente."
            value={
              <span className="whitespace-nowrap tabular-nums">
                {pctR(nsCorte.cumplimiento_pct)}
                <span className="ml-1.5 mr-3 text-[10px] font-medium uppercase tracking-[0.07em] text-fg-tertiary">
                  Cumpl.
                </span>
                {pctR(nsCorte.cumplimiento_real_pct)}
                <span className="ml-1.5 text-[10px] font-medium uppercase tracking-[0.07em] text-fg-tertiary">
                  Real
                </span>
              </span>
            }
            sub={`Acumulado ${data.corte.desde.slice(0, 4)}: ${pctR(nsAcum.cumplimiento_pct)} · REAL ${pctR(nsAcum.cumplimiento_real_pct)}`}
          />
        </Card>

        <Card className="py-4">
          <KpiCard
            label={`Facturado ${data.corte.desde.slice(0, 4)}`}
            value={copM(data.anio.facturado_total_cop)}
            sub={`${data.anio.cortes_cerrados} cortes cerrados${data.corte.en_curso && data.corte.q_key !== null ? ` + Q${data.corte.q_key.slice(-1)} en curso` : ''}`}
            trend={
              deltaAnio !== null
                ? {
                    direction: deltaAnio > 0 ? 'up' : deltaAnio < 0 ? 'down' : 'flat',
                    // Con corte en curso el backend compara contra el corte
                    // anterior TRUNCADO al mismo día (no 5 días vs 15).
                    text: `${deltaAnio > 0 ? '+' : ''}${Math.round(deltaAnio)}% vs corte anterior${data.anio.delta_misma_altura ? ` (día ${data.corte.dia_actual})` : ''}`,
                    tone: deltaAnio >= 0 ? 'ok' : 'crit',
                  }
                : undefined
            }
          />
        </Card>

        <Card className="py-4">
          <KpiCard
            label={data.corte.q_key !== null ? 'Pendiente del corte' : 'Pendiente del período'}
            hint="Pipeline vivo estimado a precio vigente — todavía NO factura. Incluye OPs con actividad desde 30 días ANTES del corte (por eso puede superar lo del puente, que solo mira el rango). Sin validar: acción del supervisor. OPs abiertas: conciliación del coordinador. Sin programar: importar la OP."
            value={copM(data.pendiente.total_cop)}
            sub={`sin validar ${copM(data.pendiente.sin_validar_cop)} · OPs abiertas ${copM(data.pendiente.ops_abiertas_cop)} · sin programar ${copM(data.pendiente.sin_programar_cop)}`}
          />
        </Card>
      </div>

      {/* ── Corte por área + Puente ── */}
      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-2">
        <Card className="p-5">
          <div className="mb-1 flex items-baseline gap-3">
            <h2 className="text-sm font-medium text-fg">
              {data.corte.q_key !== null ? `Corte Q${data.corte.q_key.slice(-1)} · ` : 'Período · '}
              {limpiar(FMT_DIA_MES.format(parseUTC(data.corte.desde)))} –{' '}
              {limpiar(FMT_DIA_MES.format(parseUTC(data.corte.hasta)))}
            </h2>
            {data.corte.en_curso && data.corte.dia_actual !== null ? (
              <span className="text-[11px] text-fg-tertiary">
                corte en curso · día {data.corte.dia_actual} de {data.corte.dias_total}
              </span>
            ) : (
              <span className="text-[11px] text-fg-tertiary">
                {data.corte.q_key !== null
                  ? 'corte cerrado'
                  : 'la facturación entra por fecha de conciliación'}
              </span>
            )}
          </div>
          {data.corte.en_curso && data.corte.dia_actual !== null ? (
            <div className="mb-4 mt-2 h-1.5 overflow-hidden rounded-sm bg-surface-overlay">
              <div
                className="h-full rounded-sm bg-fg-tertiary"
                style={{ width: `${(data.corte.dia_actual / data.corte.dias_total) * 100}%` }}
              />
            </div>
          ) : (
            <div className="mb-4" />
          )}

          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-border text-[10px] font-semibold uppercase tracking-[0.08em] text-fg-secondary">
                <th className="py-2 pr-3 text-left">Área</th>
                <th className="px-3 py-2 text-right">Unds validadas</th>
                <th className="py-2 pl-3 text-right">Valor</th>
              </tr>
            </thead>
            <tbody>
              {data.areas.map((a) => (
                <tr key={a.area_id} className="border-b border-border/40">
                  <td className="py-2.5 pr-3 text-[13px] text-fg">{a.area_nombre}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-[12px] tabular-nums text-fg-secondary">
                    {miles(a.unidades_validadas)}
                  </td>
                  <td className="py-2.5 pl-3 text-right font-mono text-[12px] tabular-nums text-fg">
                    {copM(a.valor_total)}
                  </td>
                </tr>
              ))}
              <tr className="border-b border-border/60 bg-surface-overlay/30">
                <td className="py-2.5 pr-3 text-[12px] font-semibold text-fg">Subtotal armado</td>
                <td className="px-3 py-2.5 text-right font-mono text-[12px] font-semibold tabular-nums text-fg">
                  {miles(data.facturado.unidades_validadas)}
                </td>
                <td className="py-2.5 pl-3 text-right font-mono text-[12px] font-semibold tabular-nums text-fg">
                  {copM(data.facturado.valor_total)}
                </td>
              </tr>
              <tr className="border-b border-border/40">
                <td className="py-2.5 pr-3 text-[13px] text-fg">
                  Tiendas
                  <p className="mt-0.5 font-mono text-[10px] text-fg-tertiary">
                    stickers{' '}
                    {copM(data.tiendas.ingreso_insumos > 0 ? data.tiendas.ingreso_insumos : 0)} en
                    insumos cobrados · {miles(data.tiendas.stickers)} stickers ·{' '}
                    {miles(data.tiendas.termos)} termos
                  </p>
                  <p className="font-mono text-[10px] text-fg-tertiary">
                    facturan por fecha de cada corrida, sin conciliación · v1: sin recargo dominical
                    · sin gate de validación (paridad pendiente)
                  </p>
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-[12px] tabular-nums text-fg-secondary">
                  {miles(data.tiendas.unidades)} unds
                </td>
                <td className="py-2.5 pl-3 text-right font-mono text-[12px] tabular-nums text-fg">
                  {copM(data.tiendas.valor_total)}
                </td>
              </tr>
              <tr className="border-t-2 border-border-stronger">
                <td className="py-3 pr-3 text-[12px] font-semibold uppercase tracking-[0.04em] text-fg">
                  Total corte
                </td>
                <td />
                <td className="whitespace-nowrap py-3 pl-3 text-right font-mono text-[13px] font-semibold tabular-nums text-fg">
                  {copM(facturadoCorte)}
                </td>
              </tr>
            </tbody>
          </table>
          <p className="mt-3 font-mono text-[10px] text-fg-tertiary">
            cubre ~80% del corte del cliente · aparte: stickers de línea, standby, emergentes
          </p>
          {data.puente.skus_sin_precio > 0 ? (
            <p className="mt-1 font-mono text-[10px] text-warn">
              ⚠ {data.puente.skus_sin_precio} SKUs sin precio → facturan $0 con aviso
            </p>
          ) : null}
        </Card>

        <Card className="p-5">
          <h2 className="mb-4 text-sm font-medium text-fg">Puente · producido → facturado</h2>
          <div className="flex flex-col gap-2">
            <FilaPuente
              label={`Producido válido ${limpiar(FMT_DIA_MES.format(parseUTC(data.corte.desde)))} – ${
                data.corte.en_curso
                  ? 'hoy'
                  : limpiar(FMT_DIA_MES.format(parseUTC(data.corte.hasta)))
              }`}
              valor={data.puente.producido_valido_cop}
              max={data.puente.producido_valido_cop}
              fuerte
            />
            <FilaPuente
              label="− Pendiente de validar"
              valor={data.puente.sin_validar_cop}
              max={data.puente.producido_valido_cop}
              nota="supervisor"
            />
            <FilaPuente
              label="− OPs abiertas / sin conciliar"
              valor={data.puente.ops_abiertas_cop}
              max={data.puente.producido_valido_cop}
              nota="coordinador"
            />
            <FilaPuente
              label="− OPs sin programar"
              valor={data.puente.sin_programar_cop}
              max={data.puente.producido_valido_cop}
              nota="coordinador"
            />
            <FilaPuente
              label="− Sin OP (fuera de programa)"
              valor={data.puente.sin_op_cop}
              max={data.puente.producido_valido_cop}
              nota="no factura por esta vía"
            />
            <FilaPuente
              label="+ Conciliado de cortes previos"
              valor={data.puente.conciliado_de_previos_cop}
              max={data.puente.producido_valido_cop}
              nota="producción anterior facturada en este corte"
            />
            <FilaPuente
              label={
                data.puente.residuo_cop >= 0
                  ? '+ Recargo dominical y ajustes'
                  : '− Ajustes de precio/ventana'
              }
              valor={Math.abs(data.puente.residuo_cop)}
              max={data.puente.producido_valido_cop}
              nota="×1.8 + diferencias de precio/ventana"
            />
            <div className="mt-1 border-t border-border pt-2">
              <FilaPuente
                label="= Facturado del corte"
                valor={data.puente.facturado_cop}
                max={data.puente.producido_valido_cop}
                fuerte
              />
            </div>
          </div>
          <p className="mt-4 font-mono text-[10px] text-fg-tertiary">
            cifras estimadas a precio vigente · ventanas distintas: la facturación entra por fecha
            de conciliación — un corte cerrado puede crecer
          </p>
        </Card>
      </div>

      {/* ── Nivel de servicio (sigue el período y la planta filtrados) ── */}
      <NivelServicioSemanal
        ns={ns}
        anio={data.corte.desde.slice(0, 4)}
        rango={{ desde: data.corte.desde, hasta: data.corte.hasta }}
        filtroLabel={`${limpiar(FMT_DIA_MES.format(parseUTC(data.corte.desde)))} – ${limpiar(FMT_DIA_MES.format(parseUTC(data.corte.hasta)))} · ${
          data.planta_id === null
            ? 'Todas las plantas'
            : (data.plantas.find((p) => p.id === data.planta_id)?.nombre ?? '')
        }`}
      />

      {/* ── Órdenes del corte + Tendencia ── */}
      <OrdenesDelCorte ordenes={data.ordenes} />
      <TendenciaCortes tendencia={data.tendencia} />

      <p className="mt-4 font-mono text-[10px] text-fg-tertiary">
        OPs de armado: 8 dígitos · órdenes de tienda: 10 dígitos · visible por puede_ver_costos
      </p>
    </>
  );
}

function FilaPuente({
  label,
  valor,
  max,
  nota,
  fuerte = false,
}: {
  label: string;
  valor: number;
  max: number;
  nota?: string;
  fuerte?: boolean;
}) {
  const ancho = max > 0 ? Math.max(valor > 0 ? 3 : 0, (valor / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span
        className={cn(
          'w-64 truncate text-[12px]',
          fuerte ? 'font-semibold text-fg' : 'text-fg-secondary',
        )}
      >
        {label}
      </span>
      <div className="h-2 w-24 shrink-0 overflow-hidden rounded-sm bg-surface-overlay">
        <div
          className={cn('h-full rounded-sm', fuerte ? 'bg-fg-secondary' : 'bg-fg-tertiary/60')}
          style={{ width: `${Math.min(100, ancho)}%` }}
        />
      </div>
      <span
        className={cn(
          'whitespace-nowrap text-right font-mono text-[12px] tabular-nums',
          fuerte ? 'font-semibold text-fg' : 'text-fg',
        )}
      >
        {copM(valor)}
      </span>
      {nota ? (
        <span title={nota} className="min-w-0 truncate font-mono text-[10px] text-fg-tertiary">
          · {nota}
        </span>
      ) : null}
    </div>
  );
}

// ============================================================================
// Nivel de servicio semanal (4 series + chips de área + leyendas-filtro)
// ============================================================================

type SerieNS = 'programadas' | 'realizadas' | 'cumplimiento' | 'real';

const SERIES_NS: Array<{ key: SerieNS; label: string; tipo: 'bar' | 'line'; color: string }> = [
  { key: 'cumplimiento', label: 'Cumplimiento', tipo: 'line', color: 'var(--color-info)' },
  { key: 'real', label: 'Cumplimiento REAL', tipo: 'line', color: 'var(--color-fg)' },
  { key: 'programadas', label: 'Programadas', tipo: 'bar', color: 'var(--color-border-strong)' },
  { key: 'realizadas', label: 'Realizadas', tipo: 'bar', color: 'var(--color-fg-tertiary)' },
];

function NivelServicioSemanal({
  ns,
  anio,
  rango,
  filtroLabel,
}: {
  ns: DirectivoFacturacion['nivel_servicio'];
  anio: string;
  rango: { desde: string; hasta: string };
  filtroLabel: string;
}) {
  const { diaInicioSemana, hoyOperativo } = useTenantConfig();
  const [areaSel, setAreaSel] = useState<string>('todas');
  const [ocultas, setOcultas] = useState<Set<SerieNS>>(() => new Set());
  const toggle = (k: SerieNS) =>
    setOcultas((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  const visible = (k: SerieNS) => !ocultas.has(k);

  const areas = [...new Map(ns.semanas_area.map((f) => [f.area_id, f])).values()].sort((a, b) =>
    a.area_nombre.localeCompare(b.area_nombre),
  );

  // El gráfico SIGUE el período filtrado: semanas dentro del rango; si el
  // rango cubre menos de 3 semanas ISO, cambia a vista POR DÍA (sin chips de
  // área — el desglose diario por área no existe).
  const wDesde = semanaIsoKeyDe(rango.desde, diaInicioSemana);
  const wHasta = semanaIsoKeyDe(rango.hasta, diaInicioSemana);
  const semanasRango = ns.semanas.filter((s) => s.semana_iso >= wDesde && s.semana_iso <= wHasta);
  const vistaDiaria = semanasRango.length < 3;

  const filas =
    areaSel === 'todas'
      ? semanasRango
      : ns.semanas_area.filter(
          (f) => f.area_id === areaSel && f.semana_iso >= wDesde && f.semana_iso <= wHasta,
        );
  // Máximo 12 buckets; la semana EN CURSO va achurada (mockup).
  const semanaHoy = numeroSemanaIso(hoyOperativo, diaInicioSemana);
  // TODOS los días del rango (hasta hoy): el backend omite días sin programa
  // ni OP capturada — sin el relleno el eje escondía días operados
  // (auditoría 2026-07-05) y el hueco de programa quedaba invisible.
  const diasMapa = new Map(ns.dias.map((d) => [d.fecha, d]));
  const finDiario = rango.hasta < hoyOperativo ? rango.hasta : hoyOperativo;
  const diasRango: typeof ns.dias = [];
  for (let t = Date.parse(rango.desde); t <= Date.parse(finDiario); t += 86_400_000) {
    const fecha = new Date(t).toISOString().slice(0, 10);
    diasRango.push(
      diasMapa.get(fecha) ?? {
        fecha,
        programadas: 0,
        realizadas: 0,
        cumplimiento_pct: null,
        cumplimiento_real_pct: null,
      },
    );
  }

  const data = vistaDiaria
    ? diasRango.map((d) => ({
        semana: limpiar(FMT_DIA_CORTO.format(parseUTC(d.fecha))),
        en_curso: d.fecha === hoyOperativo,
        programadas: Math.round(d.programadas),
        realizadas: Math.round(d.realizadas),
        cumplimiento: d.cumplimiento_pct !== null ? Math.round(d.cumplimiento_pct) : null,
        real: d.cumplimiento_real_pct !== null ? Math.round(d.cumplimiento_real_pct) : null,
      }))
    : filas.slice(-12).map((s) => ({
        semana: `s${Number(s.semana_iso.slice(-2))}`,
        en_curso: Number(s.semana_iso.slice(-2)) === semanaHoy,
        programadas: Math.round(s.programadas),
        realizadas: Math.round(s.realizadas),
        cumplimiento: s.cumplimiento_pct !== null ? Math.round(s.cumplimiento_pct) : null,
        real: s.cumplimiento_real_pct !== null ? Math.round(s.cumplimiento_real_pct) : null,
      }));

  return (
    <Card className="mt-5 p-5">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div>
          <h2 className="text-sm font-medium text-fg">
            Nivel de servicio {vistaDiaria ? 'diario' : 'semanal'}
          </h2>
          <p className="mt-0.5 text-[11px] text-fg-tertiary">Filtrado: {filtroLabel}</p>
        </div>
        <div className="flex-1" />
        {!vistaDiaria && areas.length > 1 ? (
          <SegmentedControl
            value={areaSel}
            onChange={setAreaSel}
            size="sm"
            options={[
              { value: 'todas', label: 'Todas' },
              ...areas.map((a) => ({ value: a.area_id, label: a.area_nombre })),
            ]}
          />
        ) : null}
        <span className="rounded border border-border px-2 py-1 font-mono text-[10px] text-fg-secondary">
          Acumulado {anio}:{' '}
          {ns.acumulado_anio.cumplimiento_pct !== null
            ? `${Math.round(ns.acumulado_anio.cumplimiento_pct)}%`
            : 'n/s'}{' '}
          · REAL{' '}
          {ns.acumulado_anio.cumplimiento_real_pct !== null
            ? `${Math.round(ns.acumulado_anio.cumplimiento_real_pct)}%`
            : 'n/s'}
        </span>
      </div>

      {data.length === 0 ? (
        <div className="flex h-56 items-center justify-center text-sm text-fg-tertiary">
          Sin OPs programadas en el período
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart
              data={data}
              margin={{ top: 18, right: 0, left: -8, bottom: 0 }}
              barGap={2}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
              <XAxis
                dataKey="semana"
                tick={{ fontSize: 10, fill: 'var(--color-fg-secondary)' }}
                axisLine={{ stroke: 'var(--color-border)' }}
                tickLine={false}
              />
              <YAxis
                yAxisId="unds"
                tickFormatter={(v) => miles(Number(v))}
                tick={{ fontSize: 10, fill: 'var(--color-fg-tertiary)' }}
                axisLine={false}
                tickLine={false}
                width={52}
              />
              <YAxis
                yAxisId="pct"
                orientation="right"
                domain={[0, 110]}
                tickFormatter={(v) => `${v}%`}
                tick={{ fontSize: 10, fill: 'var(--color-fg-tertiary)' }}
                axisLine={false}
                tickLine={false}
                width={40}
              />
              <Tooltip
                cursor={{ fill: 'var(--color-surface-overlay)' }}
                contentStyle={{
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(v, name) => {
                  const key = String(name);
                  if (key === 'Cumplimiento' || key === 'Cumplimiento REAL')
                    return [`${Math.round(Number(v))}%`, key];
                  return [`${miles(Number(v))} paq`, key];
                }}
              />
              <Bar
                yAxisId="unds"
                dataKey="programadas"
                name="Programadas"
                radius={[2, 2, 0, 0]}
                maxBarSize={22}
                hide={!visible('programadas')}
              >
                {data.map((d, i) => (
                  <Cell
                    key={i}
                    fill={d.en_curso ? 'url(#hatchEnCurso)' : 'var(--color-border-strong)'}
                  />
                ))}
              </Bar>
              <Bar
                yAxisId="unds"
                dataKey="realizadas"
                name="Realizadas"
                radius={[2, 2, 0, 0]}
                maxBarSize={22}
                hide={!visible('realizadas')}
              >
                {data.map((d, i) => (
                  <Cell
                    key={i}
                    fill={d.en_curso ? 'url(#hatchEnCurso)' : 'var(--color-fg-tertiary)'}
                  />
                ))}
              </Bar>
              <Line
                yAxisId="pct"
                dataKey="cumplimiento"
                name="Cumplimiento"
                stroke="var(--color-info)"
                strokeWidth={1.5}
                strokeDasharray="5 4"
                dot={{ r: 2.5, fill: 'var(--color-info)', strokeWidth: 0 }}
                connectNulls
                hide={!visible('cumplimiento')}
              >
                <LabelList
                  dataKey="cumplimiento"
                  position="bottom"
                  offset={8}
                  formatter={(v: unknown) =>
                    v === null || v === undefined ? '' : `${Math.round(Number(v))}%`
                  }
                  style={{ fontSize: 10, fill: 'var(--color-info)', fontWeight: 500 }}
                />
              </Line>
              <Line
                yAxisId="pct"
                dataKey="real"
                name="Cumplimiento REAL"
                stroke="var(--color-fg)"
                strokeWidth={2}
                dot={{ r: 3, fill: 'var(--color-fg)', strokeWidth: 0 }}
                connectNulls
                hide={!visible('real')}
              >
                <LabelList
                  dataKey="real"
                  position="top"
                  offset={8}
                  formatter={(v: unknown) =>
                    v === null || v === undefined ? '' : `${Math.round(Number(v))}%`
                  }
                  style={{ fontSize: 10.5, fill: 'var(--color-fg)', fontWeight: 700 }}
                />
              </Line>
            </ComposedChart>
          </ResponsiveContainer>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {SERIES_NS.map((s) => {
              const on = visible(s.key);
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => toggle(s.key)}
                  title={on ? 'Clic para ocultar esta serie' : 'Clic para mostrar esta serie'}
                  className={cn(
                    'inline-flex items-center gap-2 rounded border px-2.5 py-1 text-[11px] transition-all',
                    on
                      ? 'border-border-strong bg-surface-overlay text-fg'
                      : 'border-border text-fg-tertiary opacity-60 hover:opacity-90',
                  )}
                >
                  {s.tipo === 'line' ? (
                    <span
                      className="inline-block h-0 w-4 border-t-2"
                      style={{
                        borderColor: s.color,
                        borderStyle: s.key === 'cumplimiento' ? 'dashed' : 'solid',
                      }}
                    />
                  ) : (
                    <span
                      className="inline-block size-2.5 rounded-[2px]"
                      style={{ background: s.color }}
                    />
                  )}
                  {s.label}
                </button>
              );
            })}
            <span className="ml-auto self-center font-mono text-[10px] text-fg-tertiary">
              la semana en curso va achurada
            </span>
          </div>
        </>
      )}
    </Card>
  );
}

// ============================================================================
// Órdenes del corte
// ============================================================================

type FiltroOrden = 'problemas' | 'proceso' | 'terminadas' | 'todas';

const BADGE_ORDEN: Record<
  FacturacionOrden['estado'],
  { label: string; clase: string; title?: string }
> = {
  terminada: { label: 'TERMINADA', clase: 'border-ok/40 text-ok' },
  en_proceso: { label: 'EN PROCESO', clase: 'border-info/40 text-info' },
  sin_programar: { label: 'SIN PROGRAMAR', clase: 'border-warn/40 text-warn' },
  facturada_tienda: {
    label: 'FACTURADA · TIENDA',
    clase: 'border-border-stronger text-fg-secondary',
    title: 'Orden de tienda: factura por fecha de corrida, sin cruce (v1 — paridad pendiente)',
  },
};

function esProblema(o: FacturacionOrden): boolean {
  return (
    o.sin_precio ||
    (o.estado === 'terminada' && o.cumplimiento_pct !== null && o.cumplimiento_pct < 99.5)
  );
}

function OrdenesDelCorte({ ordenes }: { ordenes: FacturacionOrden[] }) {
  const [filtro, setFiltro] = useState<FiltroOrden>('todas');
  const [verTodas, setVerTodas] = useState(false);

  const problemas = ordenes.filter(esProblema);
  const proceso = ordenes.filter((o) => o.estado === 'en_proceso' || o.estado === 'sin_programar');
  const terminadas = ordenes.filter(
    (o) => o.estado === 'terminada' || o.estado === 'facturada_tienda',
  );
  const visibles =
    filtro === 'problemas'
      ? problemas
      : filtro === 'proceso'
        ? proceso
        : filtro === 'terminadas'
          ? terminadas
          : ordenes;
  const filas = verTodas ? visibles : visibles.slice(0, 8);

  const totProg = visibles.reduce((s, o) => s + (o.programado ?? 0), 0);
  const totUnds = visibles.reduce((s, o) => s + o.unidades, 0);
  const totValor = visibles.reduce((s, o) => s + o.valor_cop, 0);

  return (
    <Card className="mt-5 p-5">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h2 className="text-sm font-medium text-fg">Órdenes del corte</h2>
        <div className="flex-1" />
        <SegmentedControl
          value={filtro}
          onChange={(v) => {
            setFiltro(v);
            setVerTodas(false);
          }}
          size="sm"
          options={[
            { value: 'problemas', label: `Con problemas (${problemas.length})` },
            { value: 'proceso', label: `En proceso (${proceso.length})` },
            { value: 'terminadas', label: `Terminadas (${terminadas.length})` },
            { value: 'todas', label: `Todas (${ordenes.length})` },
          ]}
        />
      </div>
      {filtro === 'problemas' ? (
        <p className="-mt-2 mb-3 text-right text-[11px] text-fg-tertiary">
          filtro transversal: estas órdenes también cuentan en «En proceso» y «Terminadas»
        </p>
      ) : null}
      {visibles.length === 0 ? (
        <p className="py-6 text-center text-sm text-fg-tertiary">Sin órdenes con este filtro</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse">
            <thead>
              <tr className="border-b border-border text-[10px] font-semibold uppercase tracking-[0.08em] text-fg-secondary">
                <th className="py-2 pr-3 text-left">OP / Orden</th>
                <th className="px-3 py-2 text-left">Cliente</th>
                <th className="px-3 py-2 text-left">Área</th>
                <th className="px-3 py-2 text-left">Planta</th>
                <th className="px-3 py-2 text-right">Programado</th>
                <th className="px-3 py-2 text-right">Validado</th>
                <th className="px-3 py-2 text-right">%</th>
                <th className="px-3 py-2 text-right">Valor</th>
                <th className="py-2 pl-3 text-left">Estado</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((o, i) => {
                const badge = BADGE_ORDEN[o.estado];
                return (
                  <tr key={`${o.numero}-${i}`} className="border-b border-border/40">
                    <td className="py-2.5 pr-3 font-mono text-[12px] font-semibold tabular-nums text-fg">
                      {o.numero}
                    </td>
                    <td className="px-3 py-2.5 text-[12px] text-fg-secondary">
                      {o.cliente_nombre}
                    </td>
                    <td className="px-3 py-2.5 text-[12px] text-fg-secondary">
                      {o.area_nombre ?? 'Tiendas'}
                    </td>
                    <td className="px-3 py-2.5 text-[12px] text-fg-secondary">{o.planta_nombre}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-[12px] tabular-nums text-fg-secondary">
                      {o.programado !== null ? miles(o.programado) : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-[12px] tabular-nums text-fg">
                      {miles(o.unidades)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-[12px] tabular-nums text-fg-secondary">
                      {o.cumplimiento_pct !== null
                        ? `${Math.min(100, Math.round(o.cumplimiento_pct))}%`
                        : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-[12px] tabular-nums text-fg">
                      {o.sin_precio ? (
                        <span className="text-warn" title="SKU sin precio vigente — factura $0">
                          $0 ⚠
                        </span>
                      ) : (
                        <>
                          {copM(o.valor_cop)}
                          {o.estado === 'en_proceso' || o.estado === 'sin_programar' ? (
                            <span className="ml-1 text-[9px] text-fg-tertiary">est.</span>
                          ) : null}
                        </>
                      )}
                    </td>
                    <td className="py-2.5 pl-3">
                      <span
                        title={badge.title}
                        className={cn(
                          'whitespace-nowrap rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.06em]',
                          badge.clase,
                        )}
                      >
                        {badge.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {!verTodas && visibles.length > filas.length ? (
                <tr>
                  <td colSpan={9} className="py-2">
                    <button
                      type="button"
                      onClick={() => setVerTodas(true)}
                      className="font-mono text-[11px] text-fg-secondary underline hover:text-fg"
                    >
                      … {visibles.length - filas.length} órdenes más
                    </button>
                  </td>
                </tr>
              ) : null}
              <tr className="border-t-2 border-border-stronger">
                <td className="py-3 pr-3 text-[12px] font-semibold uppercase text-fg">Total</td>
                <td className="px-3 py-3 text-[12px] text-fg-secondary">
                  {visibles.length} grupos
                </td>
                <td colSpan={2} />
                <td className="px-3 py-3 text-right font-mono text-[12px] tabular-nums text-fg-secondary">
                  {miles(totProg)}
                </td>
                <td className="px-3 py-3 text-right font-mono text-[12px] font-semibold tabular-nums text-fg">
                  {miles(totUnds)}
                </td>
                <td />
                <td className="px-3 py-3 text-right font-mono text-[12px] font-semibold tabular-nums text-fg">
                  {copM(totValor)}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ============================================================================
// Tendencia de cortes
// ============================================================================

function TendenciaCortes({ tendencia }: { tendencia: DirectivoFacturacion['tendencia'] }) {
  const MES_CORTO = new Intl.DateTimeFormat('es-CO', { month: 'short', timeZone: 'UTC' });
  const data = tendencia.map((t) => ({
    label: `${limpiar(MES_CORTO.format(parseUTC(t.desde)))} Q${t.q_key.slice(-1)}`,
    semanal: t.valor_semanal,
    dominical: t.valor_dominical,
    total: t.valor_total,
    en_curso: t.en_curso,
  }));

  return (
    <Card className="mt-5 p-5">
      <div className="mb-3 flex flex-wrap items-baseline gap-3">
        <h2 className="text-sm font-medium text-fg">Tendencia de cortes</h2>
        <span className="text-[11px] text-fg-tertiary">
          semanal + dominical · últimos {data.length} cortes
        </span>
      </div>
      {data.length === 0 ? (
        <div className="flex h-48 items-center justify-center text-sm text-fg-tertiary">
          Sin cortes facturados aún
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={data} margin={{ top: 20, right: 0, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10.5, fill: 'var(--color-fg-secondary)' }}
                axisLine={{ stroke: 'var(--color-border)' }}
                tickLine={false}
              />
              <YAxis
                tickFormatter={(v) => copM(Number(v))}
                tick={{ fontSize: 10, fill: 'var(--color-fg-tertiary)' }}
                axisLine={false}
                tickLine={false}
                width={64}
              />
              <Tooltip
                cursor={{ fill: 'var(--color-surface-overlay)' }}
                contentStyle={{
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(v, name) => [copM(Number(v)), String(name)]}
              />
              <Bar dataKey="semanal" stackId="corte" name="Semanal" maxBarSize={36}>
                {data.map((d, i) => (
                  <Cell
                    key={i}
                    fill="var(--color-fg-tertiary)"
                    fillOpacity={d.en_curso ? 0.45 : 1}
                  />
                ))}
              </Bar>
              <Bar
                dataKey="dominical"
                stackId="corte"
                name="Dominical (×1.8)"
                radius={[2, 2, 0, 0]}
                maxBarSize={36}
              >
                {data.map((d, i) => (
                  <Cell key={i} fill="var(--color-info)" fillOpacity={d.en_curso ? 0.45 : 1} />
                ))}
              </Bar>
              <Line
                dataKey="total"
                stroke="none"
                dot={false}
                activeDot={false}
                legendType="none"
                tooltipType="none"
                isAnimationActive={false}
              >
                <LabelList
                  dataKey="total"
                  position="top"
                  offset={8}
                  formatter={(v: unknown) => (Number(v) > 0 ? copM(Number(v)) : '')}
                  style={{ fontSize: 10.5, fill: 'var(--color-fg)', fontWeight: 700 }}
                />
              </Line>
            </ComposedChart>
          </ResponsiveContainer>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="inline-flex items-center gap-1.5 text-[11px] text-fg-secondary">
              <span className="inline-block size-2.5 rounded-[2px] bg-fg-tertiary" /> Semanal
            </span>
            <span className="inline-flex items-center gap-1.5 text-[11px] text-fg-secondary">
              <span className="inline-block size-2.5 rounded-[2px] bg-info" /> Dominical (×1.8)
            </span>
            <span className="flex-1" />
            <span className="font-mono text-[10px] text-fg-tertiary">
              Q2 siempre es el corte corto (7 días) · el corte en curso va atenuado
            </span>
          </div>
        </>
      )}
    </Card>
  );
}
